import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Paper, Stack, Tooltip, Typography } from "@mui/material";
import type { FC } from "react";
import type { KpiItem, KpiSection } from "../kpiSections";
import { KPI_PALETTE } from "./kpiPalette";

interface SectionTitleProps {
  readonly section: KpiSection;
}

const SectionTitle: FC<SectionTitleProps> = ({ section }) => (
  <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
    <Typography
      variant="caption"
      sx={{
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: KPI_PALETTE[section.colorGroup].main,
      }}
    >
      {section.title}
    </Typography>
    {section.tooltip && (
      <Tooltip title={section.tooltip} arrow>
        <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary", cursor: "help" }} />
      </Tooltip>
    )}
  </Stack>
);

interface KpiCardProps {
  readonly item: KpiItem;
  readonly section: KpiSection;
  readonly compact: boolean;
}

const KpiCard: FC<KpiCardProps> = ({ item, section, compact }) => {
  const colors = KPI_PALETTE[section.colorGroup];
  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 2 : 1.5,
        height: "100%",
        bgcolor: colors.background,
        border: `1px solid ${colors.border}`,
        borderRadius: 1.5,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {compact && <SectionTitle section={section} />}
      <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.3, display: "block" }}>
        {item.label}
      </Typography>
      <Typography
        variant={compact ? "h5" : "h4"}
        sx={{ fontWeight: "bold", mt: 0.5, lineHeight: 1.1, color: colors.main }}
      >
        {item.primary}
      </Typography>
      {item.secondary && (
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, lineHeight: 1.35 }}>
          {item.secondary}
        </Typography>
      )}
      {item.sub && (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: "auto", pt: 0.75 }}>
          {item.sub}
        </Typography>
      )}
    </Paper>
  );
};

interface Props {
  readonly sections: KpiSection[];
  /** `stacked` lists each section under its own heading; `grid` flattens all cards into a 3-column grid with the
   * section title inside each card. */
  readonly layout?: "stacked" | "grid";
}

const KpiSections: FC<Props> = ({ sections, layout = "stacked" }) => {
  if (layout === "grid") {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        {sections.flatMap((section) =>
          section.items.map((item) => (
            <KpiCard key={`${section.title}-${item.label}`} item={item} section={section} compact />
          )),
        )}
      </Box>
    );
  }
  return (
    <Stack spacing={2} sx={{ mb: 3 }}>
      {sections.map((section) => (
        <Box key={section.title}>
          <Box sx={{ mb: 1 }}>
            <SectionTitle section={section} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 1.5 }}>
            {section.items.map((item) => (
              <KpiCard key={item.label} item={item} section={section} compact={false} />
            ))}
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

export default KpiSections;
